import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, HostBinding, inject, input, OnInit, signal, untracked } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import {
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDateRangeInput,
    MatDateRangePicker,
    MatEndDate,
    MatStartDate
} from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';

import { TreeAutocompleteConfigsService } from '~app/services/tree-autocomplete-configs.service';
import { getFilterOperator, getFilterOperators } from '~components/search-filter/filter/filter-operators';
import { NumberInputDirective } from '~core/directives/number-input.directive';
import { ColumnFilterModel } from '~core/models/column-filter.model';
import { ComplexDataTypesEnum } from '~core/models/complex-data-types.enum';
import { ComparisonOperatorsEnum } from '~core/models/enums/comparison-operators.enum';
import { FilterItemTypeEnum } from '~core/models/enums/filter-item-type.enum';
import { OperandTypeEnum } from '~core/models/enums/operand-type.enum';
import { FilterItemModel } from '~core/models/filter-item.model';
import { SelectOptionModel } from '~core/models/select-option.model';
import { FilterService } from '~core/services/filter.service';
import { TranslationService } from '~core/services/translation.service';
import { Tools } from '~core/utils/tools/index';
import { Utils } from '~core/utils/tools/utils.tools';
import { OptionalType } from '~shared/core/types/optional.type';
import { IconComponent } from '../../../icon/icon.component';

@Component({
    selector: 'arc-filter-part',
    templateUrl: './filter-part.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        MatFormField,
        MatSelect,
        ReactiveFormsModule,
        FormsModule,
        MatOption,
        NgTemplateOutlet,
        MatInput,
        MatDatepickerInput,
        MatSuffix,
        MatDatepickerToggle,
        MatDatepicker,
        MatDateRangeInput,
        MatStartDate,
        MatEndDate,
        MatDateRangePicker,
        NumberInputDirective,
        MatIconButton,
        MatIcon,
        MatMenuTrigger,
        MatTooltip,
        MatMenu,
        MatMenuItem,
        IconComponent,
        TranslocoPipe
    ]
})
export class FilterPartComponent implements OnInit {
    @HostBinding('class') classes = 'flex gap-2 flex-wrap';

    readonly OperandTypeEnum = OperandTypeEnum;
    readonly FilterItemTypeEnum = FilterItemTypeEnum;
    readonly ComparisonOperatorsEnum = ComparisonOperatorsEnum;
    readonly ComplexDataTypesEnum = ComplexDataTypesEnum;

    readonly filterModel = input.required<ColumnFilterModel>();
    readonly index = input.required<number>();
    readonly filterProperties = input.required<FilterItemModel[]>();

    readonly filter = {
        column: signal<OptionalType<string>>(undefined),
        value1: signal<any>(undefined),
        value2: signal<any>(undefined),
        comparisonOperator: signal<OptionalType<ComparisonOperatorsEnum>>(undefined),
        operand: signal(OperandTypeEnum.And)
    };

    readonly currentFilterProperty = computed(() => this.filterProperties().find(p => p.columnName === this.filter.column()));
    readonly currentFilterComparisonOperators = computed(() => {
        const currentFilterProperty = this.currentFilterProperty();
        if (!!currentFilterProperty) {
            const filterComparisonOperators = getFilterOperators(currentFilterProperty);
            const hasAllowedValues = !!currentFilterProperty.allowedValues;
            return filterComparisonOperators.filter(o => {
                // if there is a list of allowed values, only equal and not equal operators are allowed
                const isOperatorAllowed =
                    !hasAllowedValues || o.operator === ComparisonOperatorsEnum.Equal || o.operator === ComparisonOperatorsEnum.NotEqual;
                // if there is a shouldShowPredicate, only show operators that match the predicate
                const shouldShow = !o.shouldShowPredicate || o.shouldShowPredicate(currentFilterProperty!);
                return isOperatorAllowed && shouldShow;
            });
        } else {
            return [];
        }
    });

    readonly filterPropertiesSameGroup = computed(() => {
        const current = this.currentFilterProperty();
        const all = this.filterProperties();
        if (!current?.comparisonGroup) {
            return [];
        }

        return all.filter(p => p.comparisonGroup === current.comparisonGroup && p.columnName !== current.columnName);
    });
    readonly canCompareWithAnotherProperty = computed(() => this.filterPropertiesSameGroup().length > 0);
    readonly value1ComparedPropertyLabel = computed(() => {
        const value1 = this.filter.value1();
        const filterProperties = this.filterProperties();
        if (typeof value1 === 'string') {
            const match = value1.match(/^\{([^{}]+)\}$/);
            if (match) {
                const columnName = match[1];
                return filterProperties.find(p => p.columnName === columnName)?.label;
            }
        }

        return undefined;
    });

    readonly currentComparisonOperator = computed(() =>
        this.currentFilterComparisonOperators().find(o => o.operator === this.filter.comparisonOperator())
    );

    readonly dateFormatPlaceholder = signal('');

    readonly treeAutocompleteConfigs = inject(TreeAutocompleteConfigsService);
    readonly allowedTimeValues: SelectOptionModel<Date>[] = Tools.Time.generateSelectList();

    private readonly computedFilter = computed<Omit<ColumnFilterModel, 'internalId'>>(() => ({
        column: this.filter.column() || '',
        // eslint-disable-next-line no-null/no-null
        values: [this.filter.value1(), this.filter.value2()].filter(v => v !== undefined && v !== null),
        comparisonOperator: this.filter.comparisonOperator() || ComparisonOperatorsEnum.Empty,
        operand: this.filter.operand()
    }));

    private readonly filterService = inject(FilterService);
    private readonly translationService = inject(TranslationService);

    constructor() {
        // when filter property changes to something with allowed values, set the default operator and value
        effect(() => {
            const currentFilterProperty = this.currentFilterProperty();
            const value = this.filter.value1();

            untracked(() => {
                if (currentFilterProperty?.allowedValues) {
                    this.filter.comparisonOperator.set(ComparisonOperatorsEnum.Equal);
                    // eslint-disable-next-line eqeqeq
                    const hasValidValue = currentFilterProperty.allowedValues.some(kv => kv.key == value);

                    // set to first if not already a valid value
                    if (!hasValidValue) {
                        this.filter.value1.set(currentFilterProperty.allowedValues[0].key);
                    }
                }
            });
        });

        // Update the filter in the service whenever it changes
        effect(() => {
            const internalId = this.filterModel().internalId;
            const computedFilter = this.computedFilter();
            untracked(() => this.filterService.updateFilter(internalId, computedFilter));
        });
    }

    ngOnInit(): void {
        this.updateFilter(Utils.deepCopy(this.filterModel()));
        this.dateFormatPlaceholder.set(this.translationService.current.dateFormat);
    }

    // when column changed, reset operator and values
    handleColumnChanged(): void {
        // set default operator
        const defaultOperator = this.currentFilterComparisonOperators().find(o => o.isDefault);
        this.filter.comparisonOperator.set(defaultOperator?.operator ?? this.currentFilterComparisonOperators()[0].operator);

        // reset values
        this.filter.value1.set(undefined);
        this.filter.value2.set(undefined);
    }

    handleRemove(): void {
        this.filterService.removeFilter(this.filterModel().internalId);
    }

    shouldShowValueField(): boolean {
        const currentFilterProperty = this.currentFilterProperty();
        if (!currentFilterProperty) {
            return true;
        }

        const comparisonOperator = this.filter.comparisonOperator();
        return !!comparisonOperator && !!getFilterOperator(currentFilterProperty.type, comparisonOperator)?.expectedParameterAmount;
    }

    /**
     * selected value could be number, but allowed values are strings, so we need to compare them with doule equals
     */
    allowedValuesCompareFn(o1: any, o2: any): boolean {
        // eslint-disable-next-line eqeqeq
        return o1 == o2;
    }

    setFilterValueCompareWith(filterProperty?: FilterItemModel): void {
        this.filter.value1.set(filterProperty ? `{${filterProperty.columnName}}` : undefined);
    }

    private updateFilter(filter: ColumnFilterModel): void {
        this.filter.column.set(filter.column);
        this.filter.value1.set(filter.values[0]);
        this.filter.value2.set(filter.values[1]);
        this.filter.comparisonOperator.set(filter.comparisonOperator);
        this.filter.operand.set(filter.operand);
    }
}
